import { EntityId, PayloadAction } from "@reduxjs/toolkit"
import { addSeconds, isBefore } from "date-fns"
import { SagaIterator } from "redux-saga"
import {
  call,
  delay,
  put,
  race,
  retry,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects"
import {
  clearVNZTokensFromStorage,
  getVNZTokensFromStorage,
  saveVNZTokensToStorage,
  VNZService,
} from "~/components/cloud-provider/vnz/service/VNZService"
import { ApiException } from "~/interfaces/entities/ApiException"
import { CartEntity } from "~/interfaces/entities/Cart"
import { VNZAuthTokenEntity } from "~/interfaces/entities/VNZAuthTokenEntity"
import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { VNZSessionImage } from "~/interfaces/entities/VNZSessionImage"
import { getToaster, getTranslator } from "~/redux/context"
import { applyCouponToCartSync, getCurrentCart } from "~/redux/saga/cart"
import { getCurrentPage } from "~/redux/saga/page"
import { scrollToCartOrStoreSearch } from "~/redux/saga/processLineItems"
import {
  vnzSessionImages,
  vnzSessionImagesSelectors,
} from "~/redux/state/VNZSessionImages"
import { logger } from "~/services/Logger"
import { PrinticularApi } from "~/services/PrinticularApi"
import { seemsToBeAnImage } from "~/services/Utils"
import {
  actions,
  fetchVNZCloudImagePage,
  UpdateOrderSummarySuccess,
} from "../process"
import {
  RefreshAuthenticationTokenPayload,
  SetIsAuthenticatedPayload,
  VNZAuth,
} from "../state/VNZAuth"
import {
  LoadThumbnailForImagePayload,
  vnzImages,
  vnzImagesSelectors,
} from "../state/VNZImages"
import { RootState } from "../store"
import { getApi } from "./api"
import { getVnzService } from "./VNZService"

// Cap how many decoded thumbnail blobs are kept alive at once. Cells that scroll
// far out of view get their object URL revoked (freeing memory); the flags are
// reset so the thumbnail re-fetches if the cell is scrolled back into view. This
// keeps blob memory bounded even for libraries of thousands of images.
const THUMBNAIL_CACHE_LIMIT = 800
const loadedThumbnailIds: EntityId[] = []

export function* watchSaveVNZAuthentication() {
  yield takeEvery(
    VNZAuth.actions.setIsAuthenticated.type,
    saveVNZAuthentication
  )
}

function saveVNZAuthentication(
  action: PayloadAction<SetIsAuthenticatedPayload>
) {
  const { payload } = action
  saveVNZTokensToStorage(payload)
}

export function* watchStartVNZAccessTokenRefresher() {
  yield takeLatest(
    VNZAuth.actions.setIsAuthenticated.type,
    startVNZAccessTokenRefresher
  )
}

// refresh this long before the access token actually expires
const REFRESH_BUFFER_IN_SECONDS = 5 * 60
const REFRESH_BUFFER_IN_MS = REFRESH_BUFFER_IN_SECONDS * 1000
// floor for the refresh delay: after a transient refresh failure the expiry
// is close or past, this becomes the retry interval instead of a full cycle
const MINIMUM_REFRESH_DELAY_IN_MS = 30 * 1000

function* startVNZAccessTokenRefresher(): SagaIterator<void> {
  while (true) {
    const expiryDate: string = yield select(
      (state: RootState) => state.VNZAuth.expiryDate
    )
    const expiryTimeInMs = new Date(expiryDate).getTime()

    if (isNaN(expiryTimeInMs)) {
      // defensive: without a readable expiry the timer cannot be scheduled
      logger.error("VNZ access token refresher stopped: invalid expiry date")
      break
    }

    const delayInMs = Math.max(
      expiryTimeInMs - Date.now() - REFRESH_BUFFER_IN_MS,
      MINIMUM_REFRESH_DELAY_IN_MS
    )

    const { cancelled } = yield race({
      delayed: delay(delayInMs),
      cancelled: take(VNZAuth.actions.logout.type),
    })

    if (cancelled) {
      break
    }

    yield put(VNZAuth.actions.refreshAuthenticationToken({}))
  }
}

function getTokensFromLocalStorage(): {
  accessToken: string
  refreshToken: string
  expiresIn: number
  expiryDate: Date
  isValid: boolean
  hasRequiredData: boolean
} {
  const {
    accessToken,
    refreshToken,
    expiresIn,
    expiryDate: expiryDateString,
  } = getVNZTokensFromStorage()
  const expiryDate = expiryDateString ? new Date(expiryDateString) : new Date(0)

  const hasRequiredData = Boolean(accessToken && refreshToken && expiresIn)
  const isValid = hasRequiredData && isBefore(new Date(), expiryDate)

  return {
    accessToken,
    refreshToken,
    expiresIn,
    expiryDate,
    isValid,
    hasRequiredData,
  }
}

export function* watchRestoreVNZAuthenticationState() {
  yield takeEvery(
    VNZAuth.actions.restoreAuthenticationState.type,
    restoreVNZAuthenticationState
  )
}

export function* restoreVNZAuthenticationState(): SagaIterator<void> {
  const token = getTokensFromLocalStorage()

  if (token.isValid) {
    const { accessToken, expiryDate, expiresIn, refreshToken } = token

    yield put(
      VNZAuth.actions.setIsAuthenticated({
        accessToken,
        expiresIn,
        refreshToken,
        expiryDate: expiryDate.toISOString(),
      })
    )

    // refresh the token if expires soon
    const now = new Date()
    const isExpiredSoon = isBefore(
      expiryDate,
      addSeconds(now, REFRESH_BUFFER_IN_SECONDS)
    )

    if (isExpiredSoon) {
      yield put(VNZAuth.actions.refreshAuthenticationToken({}))
    }
  } else if (token.hasRequiredData) {
    yield put(VNZAuth.actions.refreshAuthenticationToken({}))
  }
}

export function* watchRefreshVNZAuthenticationToken() {
  yield takeLeading(
    VNZAuth.actions.refreshAuthenticationToken.type,
    refreshVNZAuthenticationToken
  )
}

const MAX_REFRESH_ATTEMPTS = 3
const REFRESH_RETRY_DELAY_IN_MS = 5_000

// a 400/401 from the refresh endpoint means the refresh token itself was
// rejected: retrying cannot change the answer
function isAuthRejection(error: unknown): boolean {
  const status = (error as ApiException)?.status
  return status === 400 || status === 401
}

function* refreshVNZAuthenticationToken(
  action: PayloadAction<RefreshAuthenticationTokenPayload>
): SagaIterator<void> {
  try {
    const api: PrinticularApi = yield call(getApi)

    const { refreshToken } = getTokensFromLocalStorage()

    if (!refreshToken) {
      yield put(
        VNZAuth.actions.setRefreshTokenError({
          refreshTokenError: "No refresh token available",
        })
      )
      return
    }

    let newTokenResult: VNZAuthTokenEntity | null = null
    for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
      try {
        newTokenResult = yield call(api.refreshVerizonAccessToken, refreshToken)
        break
      } catch (error) {
        if (isAuthRejection(error) || attempt === MAX_REFRESH_ATTEMPTS) {
          throw error
        }
        yield delay(REFRESH_RETRY_DELAY_IN_MS)
      }
    }

    if (newTokenResult) {
      const { accessToken, refreshToken, expiresIn, expiryDt } = newTokenResult

      yield put(
        VNZAuth.actions.setIsAuthenticated({
          accessToken,
          refreshToken,
          expiresIn,
          expiryDate: expiryDt,
        })
      )
    }
  } catch (error) {
    logger.error("Error refreshing VNZ access token", error)

    if (isAuthRejection(error)) {
      // the refresh token is dead, the session cannot be recovered
      yield put(VNZAuth.actions.logout())
    } else {
      // transient failure (network, 5xx): keep the stored tokens so the
      // session can recover on the next attempt or page load
      yield put(
        VNZAuth.actions.setRefreshTokenError({
          refreshTokenError:
            (error as ApiException)?.message ?? "Unknown error",
        })
      )
    }
  }
}

export function* watchProcessVNZLogout() {
  yield takeEvery(VNZAuth.actions.logout.type, processVNZLogout)
}

function* processVNZLogout(): SagaIterator<void> {
  clearVNZTokensFromStorage()

  // free all decoded blob object URLs before dropping the entities
  const allImages: VNZImageEntity[] = yield select((state: RootState) =>
    vnzImagesSelectors.selectAll(state)
  )
  allImages.forEach(image => {
    if (image.thumbnailLocalUrl) {
      URL.revokeObjectURL(image.thumbnailLocalUrl)
    }
    if (image.fullImageLocalUrl) {
      URL.revokeObjectURL(image.fullImageLocalUrl)
    }
  })
  loadedThumbnailIds.length = 0

  // remove vnz images and reset pagination/error state so the next
  // sign-in starts from the first page instead of a terminal hasMore=false
  yield put(vnzImages.actions.removeAll())
  yield put(vnzImages.actions.setCount(0))
  yield put(vnzImages.actions.setHasMore(true))
  yield put(vnzImages.actions.setHasError(false))
}

export function* watchProcessFetchVNZCloudImagePage() {
  // takeLeading: while one page fetch is running, ignore further triggers
  // (repeated end-reached events, remounts such as the desktop/mobile modal
  // swap on window resize). A dispatch after completion starts the next page,
  // gated by the hasMore flag below.
  yield takeLeading(fetchVNZCloudImagePage.type, processFetchVNZCloudImagePage)
}

const IMAGE_PAGE_SIZE = 32

function* processFetchVNZCloudImagePage(): SagaIterator<void> {
  try {
    // Skip if a load is already in flight or there is nothing left to load.
    const isAlreadyLoading = yield select(
      (state: RootState) => state.VNZImages.isLoading
    )
    const hasMoreImagesToLoad = yield select(
      (state: RootState) => state.VNZImages.hasMore
    )
    if (isAlreadyLoading || !hasMoreImagesToLoad) {
      return
    }

    const vnzService: VNZService = yield call(getVnzService)

    const accessToken = yield select(
      (state: RootState) => state.VNZAuth.accessToken
    )

    yield put(vnzImages.actions.setHasError(false))
    yield put(vnzImages.actions.setIsLoading(true))

    const currentCount = yield select(vnzImagesSelectors.selectTotal)

    const images: VNZImageEntity[] = yield retry(3, 3_000, () =>
      vnzService.fetchPageOfImages(accessToken, IMAGE_PAGE_SIZE, currentCount)
    )

    yield put(vnzImages.actions.addMany(images))
    const newCount = yield select(vnzImagesSelectors.selectTotal)
    yield put(vnzImages.actions.setCount(newCount))

    if (newCount === currentCount) {
      // the latest batch did not add any new images, so we can assume
      // there are no more images to load
      yield put(vnzImages.actions.setHasMore(false))
    }

    yield put(vnzImages.actions.setIsLoading(false))
  } catch (error) {
    logger.error(error)
    yield put(vnzImages.actions.setHasError(true))
    // reset the flag so the retry button can start a fresh attempt
    yield put(vnzImages.actions.setIsLoading(false))
  }
}

export function* watchProcessVNZImageThumbnail() {
  yield takeEvery(
    vnzImages.actions.loadThumbnailForImage.type,
    processVNZImageThumbnail
  )
}

function* processVNZImageThumbnail(
  action: PayloadAction<LoadThumbnailForImagePayload>
): SagaIterator<void> {
  const imageId = action.payload.id
  const image: VNZImageEntity = yield select((state: RootState) =>
    vnzImagesSelectors.selectById(state, imageId)
  )

  try {
    const vnzService: VNZService = yield call(getVnzService)

    const accessToken = yield select(
      (state: RootState) => state.VNZAuth.accessToken
    )

    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: { isLoadingThumbnail: true },
      })
    )

    const thumbnailLocalUrl = yield retry(3, 5_000, () =>
      vnzService.fetchThumbnail(accessToken, image)
    )

    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: {
          isLoadingThumbnail: false,
          hasLoadedThumbnail: true,
          thumbnailLocalUrl,
        },
      })
    )

    // LRU eviction of decoded thumbnail blobs to keep memory bounded.
    loadedThumbnailIds.push(image.id)
    if (loadedThumbnailIds.length > THUMBNAIL_CACHE_LIMIT) {
      const evictId = loadedThumbnailIds.shift()
      if (evictId != null && evictId !== image.id) {
        const evicted: VNZImageEntity = yield select((state: RootState) =>
          vnzImagesSelectors.selectById(state, evictId)
        )
        if (evicted?.thumbnailLocalUrl) {
          URL.revokeObjectURL(evicted.thumbnailLocalUrl)
        }
        yield put(
          vnzImages.actions.updateOne({
            id: evictId,
            changes: {
              isLoadingThumbnail: false,
              hasLoadedThumbnail: false,
              hasErrorLoadingThumbnail: false,
              thumbnailLocalUrl: undefined,
            },
          })
        )
      }
    }
  } catch (error) {
    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: {
          isLoadingThumbnail: false,
          hasLoadedThumbnail: false,
          hasErrorLoadingThumbnail: true,
        },
      })
    )
  }
}

export function* watchProcessVNZSessionImages() {
  yield takeLeading(
    vnzSessionImages.actions.processVNZSessionImages.type,
    processVNZSessionImages
  )
}

function* processVNZSessionImages(): SagaIterator<void> {
  const isProcessing = yield select(
    (state: RootState) => state.VNZSessionImages.isProcessing
  )

  const hasBeenProcessed = yield select(
    (state: RootState) => state.VNZSessionImages.hasBeenProcessed
  )

  if (isProcessing || hasBeenProcessed) {
    return
  }

  const vnzService: VNZService = yield call(getVnzService)
  const accessToken: string = yield select(
    (state: RootState) => state.VNZAuth.accessToken
  )

  // return stale session images when user hit retry
  const allSessionImages: VNZSessionImage[] = yield select((state: RootState) =>
    vnzSessionImagesSelectors.selectAll(state)
  )

  const sessionImages: VNZSessionImage[] = allSessionImages.filter(
    image => !image.hasBeenProcessed && seemsToBeAnImage(image.path)
  )

  if (sessionImages.length === 0) {
    return
  }

  yield put(vnzSessionImages.actions.setIsProcessing(true))

  const productId: EntityId = yield select(
    (state: RootState) => state.VNZSessionImages.printProduct
  )

  const page = yield call(getCurrentPage)

  for (let sessionImage of sessionImages) {
    // queue the session image for upload
    yield put(
      actions.createUploadFromFileResolver({
        pageId: page.id,
        fileResolver: async () => {
          return vnzService.fetchFullImageFromSession(accessToken, sessionImage)
        },
        productId,
      })
    )

    // mark the session image as processed
    yield put(
      vnzSessionImages.actions.updateOne({
        id: sessionImage.id,
        changes: {
          hasBeenProcessed: true,
        },
      })
    )
  }

  // all session images has been queued, mark the full session as processed
  yield put(vnzSessionImages.actions.setIsProcessing(false))
  yield put(vnzSessionImages.actions.setHasBeenProcessed(true))

  yield call(scrollToCartOrStoreSearch)
}

export function* watchUpdateMaybeApplyPendingVNZCouponCode() {
  yield takeLeading(
    actions.updateOrderSummarySuccess.type,
    maybeApplyPendingVNZCouponCode
  )
}

function* maybeApplyPendingVNZCouponCode(
  action: PayloadAction<UpdateOrderSummarySuccess>
): SagaIterator<void> {
  const {
    payload: { reasonType },
  } = action

  const couponCodes: string[] = yield select(
    (state: RootState) => state.VNZSessionImages.promoCodes
  )
  const hasCouponCodes = couponCodes && couponCodes.length > 0
  const cart: CartEntity = yield call(getCurrentCart)
  const hasAlreadyAppliedCoupon = cart.couponCodeStatus === "accepted"

  const shouldApplyPendingCoupon =
    !hasAlreadyAppliedCoupon &&
    hasCouponCodes &&
    reasonType !== "updateCouponCode"

  if (shouldApplyPendingCoupon) {
    const couponCode = couponCodes[0]
    const result = yield call(applyCouponToCartSync, {
      cartId: cart.id,
      couponCode,
    })

    if (result === "accepted") {
      const toaster = getToaster()
      const t = getTranslator()

      toaster({
        title: t("redux.saga.processVNZ.appliedCouponCode.toast.title"),
        description: t(
          "redux.saga.processVNZ.appliedCouponCode.toast.description"
        ),
        status: "success",
        duration: 6000,
        isClosable: true,
      })
      yield put(vnzSessionImages.actions.setPendingCouponStatus("accepted"))
    }
  }
}

export function* watchApplyCouponCodeSuccess() {
  yield takeLatest(actions.applyCouponCodeSuccess.type, function* () {
    yield call(maybeResetVNZCouponCodeStatus)
  })
}

function* maybeResetVNZCouponCodeStatus() {
  const couponCodes: string[] = yield select(
    (state: RootState) => state.VNZSessionImages.promoCodes
  )
  const hasCouponCodes = couponCodes && couponCodes.length > 0
  const vnzCouponCode = hasCouponCodes ? couponCodes[0] : ""

  const cart: CartEntity = yield call(getCurrentCart)
  const hasAlreadyAppliedCoupon = cart.couponCodeStatus === "accepted"
  const acceptedCouponIsDifferentFromVNZCoupon =
    cart.couponCode !== vnzCouponCode

  if (hasAlreadyAppliedCoupon && acceptedCouponIsDifferentFromVNZCoupon) {
    yield put(vnzSessionImages.actions.setPendingCouponStatus("pending"))
    yield put(vnzSessionImages.actions.setPromoCodes([]))
  }
}

export function* watchDiscardVNZCouponCodeOnFulfillmentUpdate() {
  yield takeLeading(
    actions.updateFulfillmentSuccess.type,
    maybeDiscardVNZCouponCodeOnFulfillmentUpdate
  )
}

function* maybeDiscardVNZCouponCodeOnFulfillmentUpdate() {
  const cart: CartEntity = yield call(getCurrentCart)
  const hasAlreadyAppliedCoupon = cart.couponCodeStatus === "accepted"

  const couponCodes: string[] = yield select(
    (state: RootState) => state.VNZSessionImages.promoCodes
  )
  const hasCouponCodes = couponCodes && couponCodes.length > 0

  if (hasCouponCodes && hasAlreadyAppliedCoupon) {
    yield put(vnzSessionImages.actions.setPendingCouponStatus("pending"))
    yield put(vnzSessionImages.actions.setPromoCodes([]))
  }
}
